import SectionHeader from "../../../../components/SectionHeader/SectionHeader.tsx";
import { insurerName } from "../../../../constants/insurers.ts";
import type { Advisory } from "../../../../types/lifecycle.ts";
import type { LifecycleController } from "../useLifecycle.ts";
import { CARD, InfoRow } from "./shared.tsx";

const POLICY_STATUS_LABELS: Record<string, string> = {
  issued: "Издадена",
  pending: "В обработка",
  active: "Активна",
  cancelled: "Прекратена",
  void: "Анулирана",
  unknown: "Непотвърден статус",
};
const advisoryLevel = (advisory: Advisory) => {
  const level = advisory.severity.trim().toLowerCase();
  // Some OZK messages have an ERROR prefix even when their type is WARNING.
  return level !== "critical" && /^\s*ERROR\s*:/i.test(advisory.message)
    ? "error"
    : level;
};

export function PolicyPanel({
  controller,
}: {
  controller: LifecycleController;
}) {
  const { policy, advisories, busy, can, loadDocuments, loadSchedule } =
    controller;
  const uniqueAdvisories = [
    ...new Map(
      advisories.map((advisory) => [
        JSON.stringify([
          advisory.code,
          advisoryLevel(advisory),
          advisory.message,
        ]),
        advisory,
      ])
    ).values(),
  ];
  const criticalAdvisories = uniqueAdvisories.filter(
    (advisory) => advisoryLevel(advisory) === "critical"
  );
  const errors = uniqueAdvisories.filter(
    (advisory) => advisoryLevel(advisory) === "error"
  );
  const notes = uniqueAdvisories.filter(
    (advisory) => !["critical", "error"].includes(advisoryLevel(advisory))
  );
  return (
    <>
      {policy && (
        <fieldset className={CARD}>
          <SectionHeader title="Полица" />
          <div className="p-3 pt-2">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
              <InfoRow
                label="Застраховател"
                value={insurerName(policy.provider)}
              />
              <InfoRow label="Номер" value={policy.policyNumber} />
              <InfoRow label="Зелена карта" value={policy.greenCardNumber} />
              <InfoRow
                label="Статус"
                value={POLICY_STATUS_LABELS[policy.status] ?? policy.status}
              />
              {policy.premium && (
                <InfoRow
                  label="Издадена премия"
                  value={`${policy.premium.eur.toFixed(
                    2
                  )} EUR / ${policy.premium.bgn.toFixed(2)} лв.`}
                />
              )}
            </div>

            {criticalAdvisories.length > 0 && (
              <div className="mt-3 rounded-sm border-2 border-[#AA2808] bg-red-50 p-3 text-[#AA2808] dark:bg-red-900/20">
                <p className="font-bold uppercase">
                  Важно — не издавайте повторно
                </p>
                {criticalAdvisories.map((a, i) => (
                  <p key={i} className="mt-1 text-sm">
                    {a.message}
                  </p>
                ))}
              </div>
            )}

            {errors.length > 0 && (
              <div
                role="alert"
                aria-label="Грешки от застрахователя"
                className="mt-3 rounded-sm border-2 border-[#AA2808] bg-red-50 p-3 text-[#AA2808] dark:bg-red-900/20"
              >
                <p className="font-bold">Грешки от застрахователя</p>
                {errors.map((advisory, index) => (
                  <p key={index} className="mt-1 text-sm">
                    <span className="font-mono text-xs">{advisory.code}</span>{" "}
                    {advisory.message}
                  </p>
                ))}
              </div>
            )}

            {notes.length > 0 && (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-body dark:text-bodydark">
                  Бележки от застрахователя
                </summary>
                <ul className="mt-1 list-disc pl-5">
                  {notes.map((a, i) => (
                    <li key={i}>
                      <span className="font-mono text-[10px]">{a.code}</span>{" "}
                      {a.message}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              {can("documents") && (
                <button
                  onClick={loadDocuments}
                  disabled={busy !== null}
                  className="rounded bg-gray-2 px-3 py-1.5 text-xs disabled:opacity-50 dark:bg-meta-4 dark:text-white"
                >
                  Документи
                </button>
              )}
              {can("installments.read") && (
                <button
                  onClick={loadSchedule}
                  disabled={busy !== null}
                  className="rounded bg-gray-2 px-3 py-1.5 text-xs disabled:opacity-50 dark:bg-meta-4 dark:text-white"
                >
                  Вноски
                </button>
              )}
            </div>
          </div>
        </fieldset>
      )}
    </>
  );
}
